import { useEffect } from "react";

const BRAND = "Rapid24News";
const LOGO = "/brand/logo.jpg";

function setMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setJsonLd(id, data) {
  let el = document.getElementById(id);
  if (data === null) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export function useSeo({ title, description, image, type = "website", article, breadcrumb } = {}) {
  useEffect(() => {
    const fullTitle = title ? `${title} | ${BRAND}` : `${BRAND} — Fast • Trusted • Fearless`;
    document.title = fullTitle;
    const img = image || LOGO;
    const url = window.location.href;

    setMeta("name", "description", description || "Rapid24News — Lucknow, Uttar Pradesh, India & World news. Fast, Trusted, Fearless.");
    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", description || "");
    setMeta("property", "og:image", img.startsWith("http") || img.startsWith("/") ? img : img);
    setMeta("property", "og:type", type);
    setMeta("property", "og:url", url);
    setMeta("property", "og:site_name", BRAND);
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "twitter:description", description || "");
    setMeta("name", "twitter:image", img);

    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement("link"); link.rel = "canonical"; document.head.appendChild(link); }
    link.href = url;

    setJsonLd("ld-org", {
      "@context": "https://schema.org", "@type": "NewsMediaOrganization",
      name: BRAND, url: "https://rapid24news.in", logo: "https://rapid24news.in/brand/logo.jpg",
      slogan: "Fast • Trusted • Fearless",
      email: "rapid24news.in@gmail.com",
      sameAs: [
        "https://www.youtube.com/@Rapid24News-j2u",
        "https://www.instagram.com/r24n.digital",
      ],
    });

    if (article) {
      setJsonLd("ld-article", {
        "@context": "https://schema.org", "@type": "NewsArticle",
        headline: article.title, image: article.featured_image ? [article.featured_image] : [],
        datePublished: article.published_at, dateModified: article.updated_at || article.published_at,
        author: { "@type": "Person", name: article.author },
        publisher: { "@type": "Organization", name: BRAND, logo: { "@type": "ImageObject", url: "https://rapid24news.in/brand/logo.jpg" } },
        description: article.meta_description || article.excerpt,
        articleSection: article.category,
      });
    } else {
      setJsonLd("ld-article", null);
    }

    if (article && article.video_url) {
      setJsonLd("ld-video", {
        "@context": "https://schema.org", "@type": "VideoObject",
        name: article.title, description: article.meta_description || article.excerpt,
        thumbnailUrl: article.featured_image ? [article.featured_image] : [],
        uploadDate: article.published_at, embedUrl: article.video_url,
      });
    } else {
      setJsonLd("ld-video", null);
    }

    if (breadcrumb) {
      setJsonLd("ld-breadcrumb", {
        "@context": "https://schema.org", "@type": "BreadcrumbList",
        itemListElement: breadcrumb.map((b, i) => ({ "@type": "ListItem", position: i + 1, name: b.name, item: b.url })),
      });
    } else {
      setJsonLd("ld-breadcrumb", null);
    }
  }, [title, description, image, type, article, breadcrumb]);
}
